import moment from "moment-timezone";
import i18n from "@/common/locales/i18n";
import { BreakType } from "@/common/types/breaks";

const LOCALE_TIMEZONE: Record<string, string> = {
  "pt-BR": "America/Sao_Paulo",
  "pt-PT": "Europe/Lisbon",
  "en-US": "America/New_York",
  "en-GB": "Europe/London",
  "es-ES": "Europe/Madrid",
  "es-MX": "America/Mexico_City",
};

const DEFAULT_TIMEZONE = "America/Sao_Paulo";

export function getTimezone(locale?: string): string {
  return (
    LOCALE_TIMEZONE[locale ?? i18n.global.locale.value ?? "pt-BR"] ??
    DEFAULT_TIMEZONE
  );
}

export function getHour(date?: string | null, sec = true, timezone?: string) {
  if (!date) return "--:--";

  const format = sec ? "HH:mm:ss" : "HH:mm";

  return moment.utc(date).tz(getTimezone(timezone)).format(format);
}

export function getDate(
  date?: string | null,
  format = "DD/MM/YYYY",
  response: string | null = "--/--/----",
  timezone?: string,
) {
  if (!date) return response;

  return moment.utc(date).tz(getTimezone(timezone)).format(format);
}

export function getDateAndHour(
  date?: string | null,
  format = "DD/MM/YYYY - HH:mm:ss",
  response = "--/--/---- - --:--:--",
  timezone?: string,
) {
  if (!date) return response;

  return moment.utc(date).tz(getTimezone(timezone)).format(format);
}

export function getDateISO(date: string, timezone?: string) {
  if (!date) return "";

  return moment
    .utc(date, "DD/MM/YYYY")
    .tz(getTimezone(timezone))
    .format("YYYY-MM-DDTHH:mm:ss");
}

export function getWeek(date: string | null, timezone?: string) {
  if (!date) return "";

  const dayOfWeek = moment.utc(date).tz(getTimezone(timezone)).format("dddd");

  return dayOfWeek.toLowerCase();
}

export function getDifference(
  startDate: string,
  endDate: string,
  showSeconds = true,
  timezone?: string,
) {
  const tz = getTimezone(timezone);
  const start = moment.utc(startDate).tz(tz);
  const end = moment.utc(endDate).tz(tz);

  const years = end.diff(start, "years");
  start.add(years, "years");

  const months = end.diff(start, "months");
  start.add(months, "months");

  const days = end.diff(start, "days");
  start.add(days, "days");

  const hours = end.diff(start, "hours");
  start.add(hours, "hours");

  const minutes = end.diff(start, "minutes");
  start.add(minutes, "minutes");

  const seconds = end.diff(start, "seconds");
  const secondsText = showSeconds ? ` ${seconds}s` : "";

  let text = `${minutes}m${secondsText}`;
  if (hours > 0) text = `${hours}h ${minutes}m${secondsText}`;
  if (days > 0) text = `${days}d ${hours}h ${minutes}m${secondsText}`;
  if (months > 0)
    text = `${months}M ${days}d ${hours}h ${minutes}m${secondsText}`;

  return {
    years,
    months,
    days,
    hours,
    minutes,
    seconds,
    text,
  };
}

export function getDiffBreaks(
  start_date: string,
  end_date: string,
  breaks: BreakType[],
  showSeconds = true,
  timezone?: string,
) {
  const tz = getTimezone(timezone);
  const now = moment().tz(tz);
  const startDate = moment.utc(start_date).tz(tz);
  const endDate = end_date ? moment.utc(end_date).tz(tz) : now;

  const totalDuration = moment.duration(endDate.diff(startDate));

  breaks?.forEach((item) => {
    const breakStart = moment.utc(item.start_date).tz(tz);
    const breakEnd = item.end_date ? moment.utc(item.end_date).tz(tz) : now;
    const breakDuration = moment.duration(breakEnd.diff(breakStart));
    totalDuration.subtract(breakDuration);
  });

  const secondsText = showSeconds ? ` ${totalDuration.seconds()}s` : "";
  let text = `${totalDuration.minutes()}m${secondsText}`;

  if (totalDuration.hours() > 0)
    text = `${totalDuration.hours()}h ${totalDuration.minutes()}m${secondsText}`;
  if (totalDuration.days() > 0)
    text = `${totalDuration.days()}d ${totalDuration.hours()}h ${totalDuration.minutes()}m${secondsText}`;
  if (totalDuration.months() > 0)
    text = `${totalDuration.months()}m ${totalDuration.days()}d ${totalDuration.hours()}h ${totalDuration.minutes()}m${secondsText}`;

  return {
    years: totalDuration.years(),
    months: totalDuration.months(),
    days: totalDuration.days(),
    hours: totalDuration.hours(),
    minutes: totalDuration.minutes(),
    seconds: totalDuration.seconds(),
    text,
  };
}

export function getDiffMinutes(
  startDate: string | null,
  endDate: string | null,
  timezone?: string,
) {
  if (!startDate || !endDate) return "";

  const tz = getTimezone(timezone);
  const start = moment.utc(startDate).tz(tz);
  const end = moment.utc(endDate).tz(tz);

  return end.diff(start, "minutes");
}
